import React, { useMemo, useState } from 'react';

import { Tab, Tabs } from '@mui/material';

import { isProtectedData, LockIconBadge } from 'pages/item/v2/components/ProtectedData';
import isEmpty from 'utils/is-empty';

import Cpe, { CpeProps } from './components/Cpe';
import Package, { PackageProps } from './components/Package';
import Software, { SoftwareProps } from './components/Software';
import VulnersCpe, { VulnersCpeProps } from './components/VulnersCpe';
import useStyles from './styles';

interface Props {
    cpe?: CpeProps['cpe'];
    vulnersCpe?: VulnersCpeProps['vulnersCpe'];
    affectedSoftware?: SoftwareProps['affectedSoftware'];
    affectedPackage?: PackageProps['affectedPackage'];
}

const Affected: React.FC<Props> = ({ cpe, vulnersCpe, affectedSoftware, affectedPackage }) => {
    const { classes } = useStyles();
    const [tab, setTab] = useState(
        !isEmpty(vulnersCpe?.cpe_tokens)
            ? 'vulnersCpe'
            : affectedSoftware
            ? 'affectedSoftware'
            : cpe
            ? 'cpe'
            : affectedPackage
            ? 'affectedPackage'
            : undefined
    );

    const content = useMemo(() => {
        switch (tab) {
            case 'vulnersCpe':
                return <VulnersCpe vulnersCpe={vulnersCpe} />;
            case 'cpe':
                return <Cpe cpe={cpe} />;
            case 'affectedSoftware':
                return <Software affectedSoftware={affectedSoftware} />;
            case 'affectedPackage':
                return <Package affectedPackage={affectedPackage} />;
        }
    }, [affectedPackage, affectedSoftware, vulnersCpe, cpe, tab]);

    const hasCpeProtectedData = useMemo(() => cpe?.some((item) => isProtectedData(item)), [cpe]);

    const hasAffectedSoftwareProtectedData = useMemo(
        () =>
            affectedSoftware
                ?.map((item) => Object.values(item))
                .flat()
                .some((value) => isProtectedData(value)),
        [affectedSoftware]
    );

    const hasAffectedPackageProtectedData = useMemo(
        () =>
            affectedPackage
                ?.map((item) => Object.values(item))
                .flat()
                .some((value) => isProtectedData(value)),
        [affectedPackage]
    );

    if (isEmpty(cpe) && isEmpty(vulnersCpe) && isEmpty(affectedSoftware) && isEmpty(affectedPackage)) {
        return null;
    }

    return (
        <div className={classes.affected}>
            <Tabs
                value={tab}
                textColor={affectedPackage ? 'inherit' : 'primary'}
                classes={{ indicator: classes.indicator }}
                onChange={(_event, value) => setTab(value)}
            >
                {isEmpty(vulnersCpe?.cpe_tokens) ? null : (
                    <Tab
                        value="vulnersCpe"
                        label={<LockIconBadge invisible={!hasCpeProtectedData}>{'Vulners CPE'}</LockIconBadge>}
                        className={classes.tab}
                    />
                )}
                {isEmpty(cpe) ? null : (
                    <Tab
                        value="cpe"
                        label={<LockIconBadge invisible={!hasCpeProtectedData}>{'CPE'}</LockIconBadge>}
                        className={classes.tab}
                    />
                )}
                {isEmpty(affectedSoftware) ? null : (
                    <Tab
                        value="affectedSoftware"
                        label={
                            <LockIconBadge invisible={!hasAffectedSoftwareProtectedData}>{'Software'}</LockIconBadge>
                        }
                        className={classes.tab}
                    />
                )}
                {isEmpty(affectedPackage) ? null : (
                    <Tab
                        value="affectedPackage"
                        label={
                            <LockIconBadge invisible={!hasAffectedPackageProtectedData}>
                                {'Affected Package'}
                            </LockIconBadge>
                        }
                        className={classes.tab}
                    />
                )}
            </Tabs>
            <div className={classes.tabComponent}>{content}</div>
        </div>
    );
};

export default Affected;
